// Imports for external libraries go here.
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery, useLazyQuery } from '@apollo/client';
import clsx from 'clsx';

import { Messages, RichText } from '@marriott/mi-ui-library';
import { useSession } from '@marriott/mi-headless-utils';
import { useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import { Phones, ProfileSectionElementProps, ProfileSectionRow } from './ProfileSectionElement.types';
import {
  CustomerPaymentMethod,
  GiftCardDetails as GiftCard,
  GiftCardDetails,
  useProfileStore,
} from '../../../modules/store/profileStore';
import { useAccountPersistentStore } from '../../../modules/store/accountPersistentStore';
import { useBannerMessagesStore } from '../../../modules/store/bannerMessagesStore';
import { useGiftCardStore } from '../../../modules/store/giftCardStore';

import {
  phoenixAccountGetProfileDetails,
  phoenixAccountGetProfileDetailsV2,
  phoenixAccountGetPhoneNumberVerifiedStatus,
  phoenixAccountViewGiftCards,
} from '../../../modules/graph';
import {
  PROFILE_CONSTANTS,
  PageContext,
  generateApolloClientHeaders,
  accountConstants,
  profileRememberMeURL,
  useCheckBreakpoint,
  BANNER_MSGS_KEYS,
  isMobilePhoneOtpVerrificationRequired,
  checkChinaLocale,
  logger,
  scrollToTargetClass,
  EVENT_NAME,
  DATALAYER_PAGE_EVENT,
  updateDataLayerProperties,
} from '../../../modules';

import {
  countryCodeHelper,
  renderLineItemHelper,
  sectionTitleHelper,
  getCreditCardDetail,
  getCardDetailStructure,
  checkPaymentMethods,
  giftCardObjectDataHelper,
  getAddGiftCardButtonData,
} from '../ProfileUtils/profileHelper';

import { SkeletonLoaderV2 } from '../../../molecules/SkeletonLoaderV2';
import { ProfileSectionType } from '../ProfileSection.types';
import { StyledProfileSectionElement } from './ProfileSectionElement.styles';
import profileUXLMock from '../__mock__/profileSectionUXL.modal.json';

// Use named rather than default exports.
export const ProfileSectionElement: FC<ProfileSectionElementProps> = ({
  profileSection,
  sectionClass,
  memberNumber,
  levelTypeCode,
  handleModalClick,
  isAuthorMode,
  isVariationV2 = false,
}) => {
  const { isACDLenabled, updateDatalayer } = useSession();
  const pageContext = useContext(PageContext);
  const { log } = logger({})('ProfileSectionElement');
  const { setBannerMsgs } = useBannerMessagesStore(state => state);
  const {
    profileDetails,
    setProfileDetails,
    setIsProfileUXLLoading,
    setPaymentId,
    setRevisionToken,
    setMfaOptIn,
    mfaOptIn,
    setIsRememberMeSet,
    setIsMobileNumberVerified,
    mobileNumberToGenerateOtp,
    setMobileNumberToGenerateOtp,
    setIsProfileNudgeRendered,
    hasQRVDataLayerFired,
    setHasQRVDataLayerFired,
  } = useProfileStore(state => state);
  const {
    setSelectedGiftCardSerialNumber,
    setGiftCardISOnumber,
    setProfileGiftCards,
    giftCards,
    setSelectedGiftCard,
    scrollToGiftCardSection,
    setScrollToGiftCardSection,
  } = useGiftCardStore(state => state);
  const { setProfileNudgePayload } = useAccountPersistentStore();
  const { IS_LOCAL_DEV } = process.env;
  const isQRVDisabled = pageContext?.featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_DISABLE_QRV] === true;
  const currentLocale = pageContext?.currentLocale ?? accountConstants?.DEFAULT_LANG;
  const isChinaLocale = checkChinaLocale(currentLocale);
  const sessionData = pageContext?.sessionData;
  const sessionCachedData = sessionData?.cacheData?.data;
  const dataLoaded = useRef<boolean>(false);
  const isGiftCardDataLoaded = useRef<boolean>(false);
  const giftCardsListClassName = 'account-gift-cards-list';
  const isTabletAndAboveViewPort = useCheckBreakpoint('viewportM'); // check if viewport is Tablet and above
  const router = useRouter();
  const [isIcc, setIsIcc] = useState<boolean | null>(null);
  const [isRememberLoading, setIsRememberLoading] = useState<boolean>(false);
  const [giftCardDetails, setGiftCardDetails] = useState<GiftCardDetails[]>();
  const { isRememberMe, setPartialUserInfo } = useUserDetailsGlobalStore(state => state);
  const [getPhoneNumberVerification] = useLazyQuery(phoenixAccountGetPhoneNumberVerifiedStatus);
  const { rememberMe } = pageContext;

  const isRememberMeSection =
    profileSection?.profileSectionRow &&
    profileSection?.profileSectionRow?.filter(
      section => section?.actionObject?.profileId === PROFILE_CONSTANTS?.DYNAMIC_LINE_ITEM_ID?.REMEMBER_ME
    )?.length > 0;

  const isViewGiftCardSection =
    profileSection?.profileSectionRow &&
    profileSection?.profileSectionRow?.filter(
      section => section?.actionObject?.profileId === PROFILE_CONSTANTS?.DYNAMIC_LINE_ITEM_ID?.VIEW_GIFT_CARDS
    )?.length > 0;

  const hasQRVSection =
    profileSection?.profileSectionRow &&
    profileSection?.profileSectionRow?.filter(
      section => section?.actionObject?.profileId === PROFILE_CONSTANTS?.DYNAMIC_LINE_ITEM_ID?.VERIFIED_SPECIAL_RATES
    )?.length > 0;

  useEffect(() => {
    const storedIsIcc = sessionStorage.getItem('isIcc') === 'true';
    if (!storedIsIcc) {
      const isIcc = router?.query['ic'] === 'true';
      if (isIcc) sessionStorage.setItem('isIcc', 'true');
      setIsIcc(isIcc);
    } else {
      setIsIcc(true);
    }
  }, [router?.query]);

  const handleEditModalClick = (userDetails: CustomerPaymentMethod[] | { id: string }[]) => {
    handleModalClick(PROFILE_CONSTANTS.DYNAMIC_LINE_ITEM_ID.CREDITCARD);
    setPaymentId(userDetails[0]?.id ?? '');
  };

  const handleViewGiftCardModal = (giftCard: GiftCardDetails) => {
    handleModalClick(PROFILE_CONSTANTS.DYNAMIC_LINE_ITEM_ID.VIEW_GIFT_CARDS);
    setSelectedGiftCardSerialNumber(giftCard.serialNumber || '');
    setGiftCardISOnumber(giftCard.cardISO || '');
    setSelectedGiftCard(giftCard);
  };

  const handleToggleCheckbox = async () => {
    setIsRememberLoading(true);
    try {
      const response = await fetch(profileRememberMeURL, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      if (response.status === 200) {
        setPartialUserInfo({ isRememberMe: !isRememberMe });
      }
    } catch (error: unknown) {
      // keep behavior same: no-op on error
    } finally {
      setIsRememberLoading(false);
    }
  };

  // This is to reduce the number of parameter
  const actionObjectDataHelper = (sectionRow: ProfileSectionRow) => {
    return {
      titleObject: sectionRow?.titleObject,
      actionObject: sectionRow?.actionObject,
      handleModalClick,
      isRememberMe,
      handleToggleCheckbox,
      memberNumber,
      isRememberLoading,
    };
  };

  const cardObjectDataHelper = (
    userCardDetails: CustomerPaymentMethod[],
    cardNumberLabel: ProfileSectionRow,
    profileSection: ProfileSectionType
  ) => {
    if (userCardDetails.length > 0) {
      const cardDetails = getCreditCardDetail(
        userCardDetails,
        cardNumberLabel?.titleObject?.featuredDynamicImage,
        isTabletAndAboveViewPort
      );
      const cardStructureHTML = getCardDetailStructure(
        cardDetails,
        profileSection?.profileSectionRow?.[0]?.titleObject?.defaultLabel?.toUpperCase(),
        profileSection?.profileSectionRow?.[0]?.titleObject?.expiryLabel?.toUpperCase(),
        profileSection?.profileSectionRow?.[0]?.titleObject?.cardTag?.toUpperCase(),
        profileSection?.profileSectionRow?.[0]?.titleObject?.cardDescription,
        isChinaLocale
      );

      // Ensure that the returned object matches ActionObjectDataType
      return {
        titleObject: {
          text: `text-${cardNumberLabel.actionObject.profileId}`,
          titleElement: cardStructureHTML,
        },
        actionObject: cardNumberLabel.actionObject,
        handleModalClick: () => handleEditModalClick(userCardDetails),
        isRememberMe,
        memberNumber,
        handleToggleCheckbox,
        isRememberLoading,
      };
    }

    return null; // Handle the case when there are no cards to display
  };

  //conditionalForLineItem- if false we have to show row, if true we have to check for user or country type and check if it is present in allowed countries/ user, in the end it will return the object which satisfies the condition
  const filteredProfileSectionRow = useMemo(() => {
    return profileSection?.profileSectionRow?.filter(section => {
      const { USER_TYPE, COUNTRY_TYPE, DYNAMIC_LINE_ITEM_ID } = PROFILE_CONSTANTS;
      const { allowedCountries, allowedUsers, conditionType, conditionalForLineItem, profileId } =
        section?.actionObject || {};
      //if conditionalLineItem is false, it mean no condition is required to show the row
      if (!conditionalForLineItem) {
        return true;
      }

      if (profileId === DYNAMIC_LINE_ITEM_ID.VERIFIED_SPECIAL_RATES) {
        const specialRatesAvailable =
          profileDetails?.specialRateDetails?.corporateRates?.corporateName ||
          (profileDetails?.specialRateDetails?.marriottRates &&
            profileDetails?.specialRateDetails?.marriottRates?.eligibleRateCodes?.length > 0);
        return specialRatesAvailable;
      }

      const countryCode = countryCodeHelper(profileDetails?.profileAddress, profileId, currentLocale);
      //checking if user type, then if true, checking if it lies in allowed user
      const isAllowedByUser = conditionType === USER_TYPE && allowedUsers.includes(levelTypeCode ?? '');
      //checking if country type, then if true, checking if it lies in allowed countries
      const isAllowedCountry = conditionType === COUNTRY_TYPE && allowedCountries.includes(countryCode);

      //check for isAllowedByUser and isAllowedCountry
      return isAllowedByUser || isAllowedCountry;
    });
  }, [profileDetails]);

  useEffect(() => {
    if (isAuthorMode) {
      const {
        contactInformation,
        loyaltyInformation,
        affiliateConnection,
        wallet,
        profile,
        featuresByGroup,
        communicationOptions,
        mfaOptIn,
        qualifiedRateValidation,
      } = profileUXLMock || {};
      const phoneArr = formatPhoneOptions(contactInformation?.phones);
      const { participant, friendOrFamily } = qualifiedRateValidation?.explore ?? {};
      const showExploreCTA = participant?.exploreDiscountGroup?.eligibleRateCodes?.length > 0;
      const { companyName } = qualifiedRateValidation.corporate.corporatePartner;
      const eligibleRateCodes = [
        ...new Map(
          [
            ...(participant?.exploreDiscountGroup?.eligibleRateCodes || []),
            ...(friendOrFamily?.exploreDiscountGroup?.eligibleRateCodes || []),
          ].map(item => [item.code, item])
        ).values(),
      ];
      setProfileDetails({
        profileAddress: contactInformation?.addresses,
        linkedAccount: [
          loyaltyInformation?.rewards?.ownership?.[0]?.code,
          affiliateConnection?.edges?.[0]?.node?.affiliate?.code,
        ],
        paymentMethods: wallet?.paymentMethods,
        preferredLanguage: profile?.preferredLanguage,
        consentFeatures: featuresByGroup?.preferencesFeatures,
        phoneNumber: phoneArr,
        communicationOptions: communicationOptions,
        specialRateDetails: {
          showExploreCTA,
          corporateRates: {
            corporateName: companyName,
          },
          marriottRates: {
            eligibleRateCodes,
          },
        },
      });
      setMfaOptIn(mfaOptIn);
      setProfileGiftCards(wallet.giftCards);
      const chinaUserPhoneNumber = isMobilePhoneOtpVerrificationRequired(phoneArr, currentLocale);

      if (chinaUserPhoneNumber) {
        setMobileNumberToGenerateOtp(chinaUserPhoneNumber);
        setIsMobileNumberVerified(false);
        setIsProfileNudgeRendered(true);
      }
    }
  }, []);

  useEffect(() => {
    if (rememberMe && isRememberMeSection) {
      setIsRememberMeSet(true);
      setPartialUserInfo({ isRememberMe: rememberMe });
    }
  }, []);

  useEffect(() => {
    if (giftCards && giftCards.length > 0) {
      setGiftCardDetails(giftCards);
    }
  }, [giftCards]);

  const formatPhoneOptions = (data: Phones[]) => {
    const formattedOutput = [];
    for (const phoneOption of data) {
      const countryCode = phoneOption.country?.code;
      const phoneNumber = phoneOption.number;
      const phoneType = phoneOption.type?.code;

      const formattedEntry = {
        countryCode,
        phoneNumber,
        phoneType,
      };

      formattedOutput.push(formattedEntry);
    }
    return formattedOutput;
  };

  // Memoize all variables that affect the query,
  // to prevent re-triggering useQuery if component re-renders.
  const skipQuery =
    useMemo(() => {
      return !sessionData && !isAuthorMode;
    }, [pageContext, isAuthorMode]) ||
    dataLoaded.current ||
    isAuthorMode ||
    profileDetails?.isdataLoaded;
  // *************** UXL ****************************

  const fetchPhoneNumberVerificationAuth = () => {
    /**
     * api to check if auth is available for user or not
     */
    getPhoneNumberVerification({
      variables: {
        rewardNumber: sessionCachedData?.rewardsId || '',
      },
      context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
      fetchPolicy: 'no-cache',
      onCompleted: data => {
        setIsMobileNumberVerified(data?.customerAuthMetadata?.isMobileAuthEnabled);
        if (data?.customerAuthMetadata?.isMobileAuthEnabled === false) {
          /**
           * if mobile number is not verified then show the label and link
           */
          setIsProfileNudgeRendered(!data?.isMobileAuthEnabled);
        }
      },
      onError: () => {
        /**
         * in error case reset rhe values
         */
        setIsMobileNumberVerified(false);
        setIsProfileNudgeRendered(false);
      },
    });
  };

  useEffect(() => {
    if (mobileNumberToGenerateOtp && !isAuthorMode) {
      /**
       * basic check for china user
       * is phone number and currentLocale
       */
      fetchPhoneNumberVerificationAuth();
    }
  }, [mobileNumberToGenerateOtp]);

  useEffect(() => {
    if (hasQRVSection && profileDetails.qrvFailed && !hasQRVDataLayerFired) {
      setHasQRVDataLayerFired(true);
      const dataLayerProps = {
        qrv_failed: true,
      };
      const dataLayerEvent = isACDLenabled
        ? {
            eventName: EVENT_NAME.QRV_FAILURE,
            update: updateDatalayer,
          }
        : undefined;

      updateDataLayerProperties(dataLayerProps, DATALAYER_PAGE_EVENT.QRV_FAILURE, dataLayerEvent);
    }
  }, [hasQRVDataLayerFired, profileDetails.qrvFailed]);

  // Use V2 query (without QRV) when QRV is disabled, otherwise use the original query
  const profileDetailsQuery = isQRVDisabled ? phoenixAccountGetProfileDetailsV2 : phoenixAccountGetProfileDetails;

  const { loading, error: profileDetailsError } = useQuery(profileDetailsQuery, {
    variables: {
      customerId: sessionCachedData?.consumerID,
    },
    context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
    skip: skipQuery,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onCompleted: (data: any) => {
      setProfileNudgePayload({
        isNudgeVerify: false,
        transactionId: '',
        communicationToken: '',
        fourDigitOfMobile: '',
      });
      dataLoaded.current = true;
      const {
        contactInformation,
        loyaltyInformation,
        affiliateConnection,
        wallet,
        profile,
        communicationOptions,
        qualifiedRateValidation,
      } = data?.customer || {};
      if (isIcc) {
        const paymentStatus = checkPaymentMethods(wallet?.paymentMethods);
        if (paymentStatus === 'success') {
          setBannerMsgs(BANNER_MSGS_KEYS?.ICC_SUCCESS_MSG);
        } else if (paymentStatus === 'pending') {
          setBannerMsgs(BANNER_MSGS_KEYS?.ICC_PENDING_MSG);
        }
      }

      const phonoeArr = formatPhoneOptions(contactInformation?.phones || []);
      const chinaUserPhoneNumber = isMobilePhoneOtpVerrificationRequired(phonoeArr, currentLocale);

      // QRV-related logic
      const { participant, friendOrFamily } = qualifiedRateValidation?.explore ?? {};
      const { corporate } = qualifiedRateValidation ?? {};
      const showExploreCTA = participant?.exploreDiscountGroup?.eligibleRateCodes?.length > 0;
      const eligibleRateCodes = [
        ...new Map(
          [
            ...(participant?.exploreDiscountGroup?.eligibleRateCodes || []),
            ...(friendOrFamily?.exploreDiscountGroup?.eligibleRateCodes || []),
          ].map(item => [item.code, item])
        ).values(),
      ];
      const errorNode =
        [participant, friendOrFamily, corporate, corporate?.corporatePartner].some(node => node?.errors?.length > 0) ||
        false;

      if (chinaUserPhoneNumber) {
        setMobileNumberToGenerateOtp(chinaUserPhoneNumber);
      }
      setProfileDetails({
        ...profileDetails,
        profileAddress: contactInformation?.addresses,
        linkedAccount: [
          loyaltyInformation?.rewards?.ownership?.[0]?.code,
          affiliateConnection?.edges?.[0]?.node?.affiliate?.code,
        ],
        paymentMethods: wallet?.paymentMethods,
        preferredLanguage: profile?.preferredLanguage,
        isdataLoaded: true,
        phoneNumber: phonoeArr,
        communicationOptions: communicationOptions,
        qrvFailed: !!errorNode,
        specialRateDetails: {
          showExploreCTA,
          marriottRates: {
            eligibleRateCodes,
          },
          ...(corporate?.corporatePartner?.companyName && {
            corporateRates: {
              corporateName: corporate.corporatePartner.companyName,
            },
          }),
        },
      });
      setRevisionToken(data?.customer?.revisionToken ?? '');
      setMfaOptIn(data?.customer?.mfaOptIn ?? '');
      setIsProfileUXLLoading(false);
    },
    onError: () => {
      setIsProfileUXLLoading(false);
      setProfileNudgePayload({
        isNudgeVerify: false,
        transactionId: '',
        communicationToken: '',
        fourDigitOfMobile: '',
      });
    },
  });

  // Separate query for gift cards - use different skip logic
  const skipGiftCardQuery =
    useMemo(() => {
      return !sessionData && !isAuthorMode;
    }, [sessionData, isAuthorMode]) ||
    isGiftCardDataLoaded.current ||
    isAuthorMode ||
    !isViewGiftCardSection;

  const { loading: giftCardsLoading } = useQuery(phoenixAccountViewGiftCards, {
    variables: {
      input: {
        customerId: sessionCachedData?.consumerID,
        email: sessionCachedData?.email,
      },
    },
    context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
    skip: skipGiftCardQuery,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onCompleted: (data: any) => {
      const giftCards =
        data?.commerce?.payment?.giftcard?.viewGiftCards?.cards?.edges?.map((edge: { node: GiftCard }) => edge.node) ||
        [];
      setProfileGiftCards(giftCards);
      isGiftCardDataLoaded.current = true;
    },
    onError: (error: Error) => {
      isGiftCardDataLoaded.current = false;
      log.error('Error fetching gift cards:', error);
      setProfileGiftCards([]);
    },
  });

  useEffect(() => {
    if (loading || giftCardsLoading) {
      setIsProfileUXLLoading(true);
    }
  }, [loading, giftCardsLoading]);

  useEffect(() => {
    if (scrollToGiftCardSection && !giftCardsLoading && !loading) {
      setTimeout(() => {
        scrollToTargetClass(giftCardsListClassName);
      }, 100);
      setScrollToGiftCardSection?.(false);
    }
  }, [scrollToGiftCardSection, giftCardsLoading, loading]);

  if (profileDetailsError) {
    return (
      <div data-testid="uxl-error-msg-wrapper">
        <Messages messageType="warning" className="my-4">
          <RichText text={pageContext?.uxlErrorMessage} componentId="uxl-error-msg" />
        </Messages>
      </div>
    );
  }

  const showSection = profileSection?.showForUsers?.some(
    user => user.toUpperCase() === PROFILE_CONSTANTS.ALL || user.toUpperCase() === levelTypeCode
  );

  return (
    <StyledProfileSectionElement>
      {loading && giftCardsLoading && !isAuthorMode ? (
        <div className={clsx('profile-sections-wrapper container px-0', sectionClass)} data-testid="profile-wrapper">
          <SkeletonLoaderV2 className="col-12 col-md-3 mb-3 skeleton-style skeleton-style-header" />
          <section className="p-4 p-md-5">
            <SkeletonLoaderV2 className="mb-4 skeleton-style skeleton-style-line-item" />
            <div className="section-border"></div>
            <SkeletonLoaderV2 className="mt-4 skeleton-style skeleton-style-line-item" />
          </section>
        </div>
      ) : (
        showSection && (
          <div
            className={clsx(
              'profile-sections-wrapper',
              sectionClass,
              isVariationV2 ? 'profile-section-element-v2' : ''
            )}
            data-testid="profile-wrapper"
          >
            {profileSection?.sectionTitle &&
              sectionTitleHelper(
                profileSection?.sectionTitle,
                isViewGiftCardSection ? giftCardsListClassName : '',
                isVariationV2
              )}
            <section className={clsx('section-wrapper', isVariationV2 ? 'px-md-4 px-3 py-4' : 'p-4 p-md-5')}>
              {filteredProfileSectionRow &&
                filteredProfileSectionRow?.map((profileRow, index) => {
                  const actionObjectData = actionObjectDataHelper(profileRow);

                  const isCardSection =
                    actionObjectData?.actionObject?.profileId === PROFILE_CONSTANTS.DYNAMIC_LINE_ITEM_ID.CREDITCARD;
                  const isGiftCardSection =
                    actionObjectData?.actionObject?.profileId === PROFILE_CONSTANTS.DYNAMIC_LINE_ITEM_ID.GIFT_CARDS;

                  const showCTAInfoText =
                    actionObjectData?.actionObject?.profileId ===
                      PROFILE_CONSTANTS.DYNAMIC_LINE_ITEM_ID.TRUSTED_DEVICES && !mfaOptIn;
                  // Handle gift card section (exactly like credit cards)
                  if (isGiftCardSection) {
                    // Get AEM-driven max cards limit
                    const maxGiftCards = profileRow?.titleObject?.maxGiftCards
                      ? Number(profileRow?.titleObject?.maxGiftCards)
                      : undefined;

                    // Filter and sort gift cards
                    const filteredGiftCards =
                      giftCardDetails?.filter(
                        card =>
                          card.cardDescription !== PROFILE_CONSTANTS.GIFT_CARD_DESCRIPTION.STORED_VALUE &&
                          card.cardStatus !== PROFILE_CONSTANTS.GIFT_CARD_STATUS.CANCELED &&
                          card.cardStatus !== PROFILE_CONSTANTS.GIFT_CARD_STATUS.CANCELLED_FOR_REMITTANCE
                      ) || [];

                    // Sort by most recent (if timestamp available)
                    const sortedGiftCards = filteredGiftCards.sort((a: GiftCard, b: GiftCard) => {
                      if (a.createTimestamp && b.createTimestamp) {
                        return new Date(b.createTimestamp).getTime() - new Date(a.createTimestamp).getTime();
                      }
                      return 0;
                    });

                    // Limit to AEM-driven max cards
                    const cardsToDisplay = sortedGiftCards.slice(0, maxGiftCards);

                    // Render gift cards (exactly like credit cards)
                    const giftCardObjects = cardsToDisplay.map((giftCard: GiftCard) => {
                      return giftCardObjectDataHelper(
                        [giftCard],
                        profileRow,
                        profileSection,
                        handleViewGiftCardModal,
                        isTabletAndAboveViewPort
                      );
                    });

                    // Calculate total items in this section (gift cards + Add button)
                    const totalGiftCardSectionItems = cardsToDisplay.length + 1; // +1 for Add button

                    return giftCardObjects.map((giftCardObject, cardIndex) => {
                      return renderLineItemHelper(
                        giftCardObject,
                        cardIndex,
                        totalGiftCardSectionItems, // Use total count including Add button
                        profileDetails,
                        isTabletAndAboveViewPort
                      );
                    });
                  }

                  // Handle credit card sections
                  if (isCardSection) {
                    const numberOfPaymentMethods = profileDetails?.paymentMethods?.length || 0;
                    if (
                      index === 0 &&
                      (!profileDetails?.paymentMethods || profileDetails?.paymentMethods.length === 0)
                    ) {
                      return null;
                    }

                    const preferredCard = profileDetails?.paymentMethods?.find(
                      (card: CustomerPaymentMethod) => card?.preferred
                    );

                    if (preferredCard) {
                      const actionCardObjectData = cardObjectDataHelper([preferredCard], profileRow, profileSection);

                      const remainingCardsToDisplay = profileDetails?.paymentMethods
                        .filter((card: CustomerPaymentMethod) => !card?.preferred)
                        .slice(0, 9);

                      const remainingCardObjects = remainingCardsToDisplay.map((card: CustomerPaymentMethod) => {
                        return cardObjectDataHelper([card], profileRow, profileSection);
                      });

                      return [actionCardObjectData, ...remainingCardObjects].map((cardObject, card) => {
                        return renderLineItemHelper(
                          cardObject,
                          card - 1,
                          numberOfPaymentMethods,
                          profileDetails,
                          isTabletAndAboveViewPort
                        );
                      });
                    } else {
                      const cardsToDisplay = profileDetails?.paymentMethods?.slice(0, 10);
                      return cardsToDisplay?.map((card: CustomerPaymentMethod, cardIndex: number) => {
                        const actionCardObjectData = cardObjectDataHelper([card], profileRow, profileSection);
                        return renderLineItemHelper(
                          actionCardObjectData,
                          cardIndex - 1,
                          numberOfPaymentMethods,
                          profileDetails,
                          isTabletAndAboveViewPort
                        );
                      });
                    }
                  } else {
                    // Check if this is the Add Card button and we're at max credit cards (10)
                    const isAddCardButton =
                      actionObjectData?.actionObject?.profileId === PROFILE_CONSTANTS.DYNAMIC_LINE_ITEM_ID.ADD_CARD;

                    if (isAddCardButton && profileDetails?.paymentMethods?.length >= 10) {
                      // Hide "Add a Card" button when user has 10 or more credit cards
                      return null;
                    }

                    // Check if this is the Add Gift Card button and we're at max cards
                    const isAddGiftCardButton =
                      actionObjectData?.actionObject?.profileId ===
                      PROFILE_CONSTANTS.DYNAMIC_LINE_ITEM_ID.ADD_GIFT_CARD;

                    if (isAddGiftCardButton) {
                      // Get the filtered gift cards count to check if we're at max
                      const filteredGiftCards =
                        giftCardDetails?.filter(
                          card =>
                            card.cardDescription !== PROFILE_CONSTANTS.GIFT_CARD_DESCRIPTION.STORED_VALUE &&
                            card.cardStatus !== 'CANCELLED'
                        ) || [];

                      const { modifiedActionObjectData, addButtonRowIndex } = getAddGiftCardButtonData(
                        actionObjectData,
                        profileRow,
                        filteredGiftCards
                      );

                      // Use the same total count as the gift card section
                      const totalGiftCardSectionItems = filteredGiftCards.length + 1; // +1 for the add button itself

                      return renderLineItemHelper(
                        modifiedActionObjectData,
                        addButtonRowIndex,
                        totalGiftCardSectionItems,
                        profileDetails,
                        isTabletAndAboveViewPort
                      );
                    }

                    return renderLineItemHelper(
                      actionObjectData,
                      index,
                      filteredProfileSectionRow?.length,
                      profileDetails,
                      isTabletAndAboveViewPort,
                      showCTAInfoText
                    );
                  }
                })}
            </section>
          </div>
        )
      )}
    </StyledProfileSectionElement>
  );
};
